<script lang="ts" setup>
// ================================
// IMPORTS
// ================================
// Vue
import { computed, ref } from "vue"
import { useRoute } from "vue-router"
import { useI18n } from "vue-i18n"

// Store
import { useBuildingStore } from "@core/stores/building-v2.store"
import { useAuthStore } from "@core/stores/auth.store"
import { METER_TYPES_LIST } from "@core/constants"
import { storeToRefs } from "pinia"
import { useEventBus } from "@vueuse/core"
import { useSourceStore } from "@core/stores/source.store"

// Components
import type { SourceTypeChoice } from "@core/components/source/form/append/machine/source-append.machine"

const buildingStore = useBuildingStore()
const sourceStore = useSourceStore()
const authStore = useAuthStore()

const {
    isFetchingBuilding,
    isFetchingBuildingSources,
    getMeters,
    getBuildingIsViewOnly,
} = storeToRefs(buildingStore)

const isFetching = computed(() => {
    return isFetchingBuilding.value || isFetchingBuildingSources.value
})

const route = useRoute()

const search = ref("")

const visibleMenu = ref(false)

const selectedType = ref("all")

/** Ouverture de la modal d'ajout de source (v6) */
const sourceAddModalVisible = ref(false)
/** Type présélectionné à l'ouverture (pdl, pce ou non défini = choix) */
const sourceAddModalFromState = ref<SourceTypeChoice | undefined>(undefined)
/** Clé pour forcer le remontage de la modal avec les bons paramètres */
const sourceAddModalKey = computed(
    () =>
        `source-add-${route.params?._id_building ?? ""}-${sourceAddModalFromState.value ?? "all"}-${sourceAddModalVisible.value}`
)

/** Skeleton du nouveau compteur pendant un rafraîchissement silencieux après le parcours d’ajout */
const isPendingNewMeterSkeleton = ref(false)

const { t } = useI18n({
    messages: {
        fr: {
            buildings: "Bâtiments",
            folder: "Dossier",
            sources: "Compteurs",
            title: "Compteurs",
            new_source: "Nouveau compteur",
            gaz: "Gaz",
            elec: "Électricité",
            all: "Tout",
            rcu: "Réseau urbain",
            eau: "Eau",
            waiting: "En attente",
            empty_message:
                "Vous n'avez pas de compteurs associés à ce bâtiment.",
        },
    },
})

const bus = useEventBus<string>("update:source_informations")
bus.on((sourceId: string) => {
    // Vérifie si la source appartient au bâtiment courant
    const source = buildingStore.getMeters.find(
        (s) => s._id_source === sourceId
    )
    if (source) {
        sourceStore.fetchSourceById({
            _id_source: sourceId,
        })
    }
})

/**
 * Ouvre la modal d'ajout de source (v6) avec optionnellement un type présélectionné
 */
function openSourceAddModal(fromState?: SourceTypeChoice): void {
    if (getBuildingIsViewOnly.value) return
    sourceAddModalFromState.value = fromState
    sourceAddModalVisible.value = true
    visibleMenu.value = false
}

/**
 * Fin du parcours PDL/PCE : bâtiment puis uniquement la nouvelle source si l’id est connu.
 * Sans `_id_source`, repli sur le chargement de tous les compteurs du bâtiment.
 */
async function onSourceAddDone(_id_source?: string): Promise<void> {
    sourceAddModalVisible.value = false
    const buildingId = route.params?._id_building as string | undefined
    if (!buildingId) return
    isPendingNewMeterSkeleton.value = true
    try {
        await buildingStore.fetchBuilding(buildingId, { silent: true })
        if (_id_source) {
            await sourceStore.fetchSourceById({
                _id_source,
                silent: true,
            })
        } else {
            await buildingStore.fetchBuildingSources({
                sourceTypes: METER_TYPES_LIST,
                silent: true,
                force: true,
            })
        }
    } finally {
        isPendingNewMeterSkeleton.value = false
    }
}

function onSourceAddCancel(): void {
    sourceAddModalVisible.value = false
}

const MENU_ADD_SOURCE = [
    {
        label: t("elec"),
        icon: "pi pi-bolt",
        command: () => openSourceAddModal("pdl"),
    },
    {
        label: t("gaz"),
        icon: "fa-light fa-fire-flame-curved",
        command: () => openSourceAddModal("pce"),
    },
]

function onClickAddSource() {
    openSourceAddModal(undefined)
}

const getSelectButton = computed(() => {
    const items = []
    items.push({
        label: `${t("all")} (${getMeters.value?.length})`,
        value: "all",
    })
    if (
        getMeters.value?.some((source) =>
            ["pdl", "elecsavoie"].includes(source.type)
        )
    ) {
        items.push({
            label: `${t("elec")} (${
                getMeters.value?.filter((source) =>
                    ["pdl", "elecsavoie"].includes(source.type)
                ).length
            })`,
            value: "pdl",
        })
    }
    if (
        getMeters.value?.some((source) =>
            ["pce", "regaz"].includes(source.type)
        )
    ) {
        items.push({
            label: `${t("gaz")} (${
                getMeters.value?.filter((source) =>
                    ["pce", "regaz"].includes(source.type)
                ).length
            })`,
            value: "pce",
        })
    }
    if (
        getMeters.value?.some((source) =>
            ["cpcu", "dalkia"].includes(source.type)
        )
    ) {
        items.push({
            label: `${t("rcu")} (${
                getMeters.value?.filter((source) =>
                    ["cpcu", "dalkia"].includes(source.type)
                ).length
            })`,
            value: "rcu",
        })
    }

    if (
        getMeters.value?.some((source) => ["eaubordeaux"].includes(source.type))
    ) {
        items.push({
            label: `${t("eau")} (${
                getMeters.value?.filter((source) =>
                    ["eaubordeaux"].includes(source.type)
                ).length
            })`,
            value: "eau",
        })
    }

    return items
})

const getMetersByCategory = computed(() => {
    if (selectedType.value === "pce") {
        return getMeters.value?.filter((source) =>
            ["pce", "regaz"].includes(source.type)
        )
    } else if (selectedType.value === "pdl") {
        return getMeters.value?.filter((source) =>
            ["pdl", "elecsavoie"].includes(source.type)
        )
    } else if (selectedType.value === "rcu") {
        return getMeters.value?.filter((source) =>
            ["cpcu", "dalkia"].includes(source.type)
        )
    } else if (selectedType.value === "eau") {
        return getMeters.value?.filter((source) =>
            ["eaubordeaux"].includes(source.type)
        )
    }
    return getMeters.value || []
})

/**
 * TODO utiliser une fuseOptions
 *
 * @example
 * import Fuse from 'fuse.js'
 *
 * const fuseOptions = {
        shouldSort: true,
        tokenize: true,
        matchAllTokens: true,
        findAllMatches: true,
        threshold: 0.3, // Tolérance de recherche (0 = exact, 1 = très permissif)
        location: 0,
        distance: 500,
        maxPatternLength: 32,
        minMatchCharLength: 1,
        keys: [
            { name: 'name' }, // Nom du compteur
            { name: 'idsource' }, // Identifiant source
            { name: 'idtype' }, // Type de compteur (PDL, PCE, CPCU)
            { name: 'offer.nom_offre' }, // Nom de l'offre d'énergie
        ],
    }
 */
const getMetersBySearch = computed(() => {
    if (search.value) {
        const matches = [
            ...getMetersByCategory.value.filter((source) =>
                source.name.toLowerCase().includes(search.value)
            ),
            ...getMetersByCategory.value.filter((source) =>
                source._id_source.toLowerCase().includes(search.value)
            ),
            ...getMetersByCategory.value.filter((source) =>
                source.address?.street?.toLowerCase().includes(search.value)
            ),
            ...getMetersByCategory.value.filter((source) =>
                source.address?.city?.toLowerCase().includes(search.value)
            ),
            ...getMetersByCategory.value.filter((source) =>
                source.address?.zipcode?.toLowerCase().includes(search.value)
            ),
        ]

        const uniqueSourcesMap = new Map()
        for (const source of matches) {
            uniqueSourcesMap.set(source._id_source, source)
        }

        return Array.from(uniqueSourcesMap.values())
    }
    return getMetersByCategory.value
})
</script>

<template>
    <div class="page page-building-meter">
        <HeaderPageBuilding
            :title="t('title')"
            class-header="flex justify-between items-center flex-wrap gap-4"
        >
            <div class="relative inline-block">
                <BaseSplitButton
                    v-if="
                        authStore.isClientHomeys &&
                        authStore.isEmployeWriteOrHigher &&
                        !getBuildingIsViewOnly
                    "
                    @click="onClickAddSource"
                    :model="MENU_ADD_SOURCE"
                    icon="pi pi-plus"
                    :label="t('new_source')"
                >
                    <template #menuitemicon="{ item: { icon } }">
                        <BaseIcon :icon="icon" />
                    </template>
                </BaseSplitButton>
            </div>
        </HeaderPageBuilding>
        <div
            class="flex md:flex-row flex-col justify-start md:justify-between md:items-center gap-4"
            v-if="!isFetching && getMeters?.length"
        >
            <BaseSelectButton
                v-model="selectedType"
                :options="getSelectButton"
                option-label="label"
                option-value="value"
            />
            <BaseInputText
                v-model="search"
                class="meter-container-filter__search"
                placeholder="Rechercher..."
                dense
                :icon="{
                    side: 'left',
                    name: ['fal', 'magnifying-glass'],
                    size: 14,
                }"
                delayed
            />
        </div>
        <BaseSkeleton v-else-if="isFetching" width="100%" height="3rem" />
        <div v-if="isFetching">
            <div v-for="index in 3" :key="index">
                <BaseSkeleton class="mb-4" width="100%" height="40rem" />
            </div>
        </div>
        <div
            v-else-if="!getMeters?.length"
            class="min-h-80 flex flex-col items-center justify-center gap-4 py-12"
        >
            <p class="text-gray-600 text-center">
                {{ t("empty_message") }}
            </p>
        </div>
        <div v-else class="flex flex-col gap-4">
            <div v-for="source in getMetersBySearch" :key="source._id_source">
                <MeterCard :meter-id="source._id_source" />
            </div>
            <BaseSkeleton
                v-if="isPendingNewMeterSkeleton"
                class="mb-4"
                width="100%"
                height="40rem"
            />
        </div>

        <!-- Modal d'ajout de compteur (PDL / PCE uniquement) -->
        <SourceAppendDialog
            v-model:visible="sourceAddModalVisible"
            :key="sourceAddModalKey"
            :building-id="(route.params?._id_building as string) ?? undefined"
            :from-state="sourceAddModalFromState"
            :allowed-source-types="['pdl', 'pce']"
            :address="buildingStore.building?.address ?? null"
            @done="onSourceAddDone"
            @cancel="onSourceAddCancel"
        />
    </div>
</template>
